/**
 * Shared PII + secret scrubbing. Runtime-compatible with Node, Workers AND the
 * browser — it moved here from `ai/rewrite-privacy.js` in IR-0 because the
 * diagnostics path imports it client-side, and `scripts/ai/` is excluded from
 * the Pages static copy (build-pages.js EXCLUDED_DIR_NAMES).
 *
 * Two layers, and they are NOT interchangeable:
 *
 *  - `scrubGeneric(text)` needs no context and can run at CAPTURE time, before
 *    a project is even loaded. It catches secrets and the shapes of emails and
 *    phone numbers.
 *  - `scrubRewriteText(text, {clientInfo, projectName})` additionally redacts
 *    THIS project's client values, and can only run once they are known.
 *
 * A diagnostics buffer must run BOTH, at both times: breadcrumbs are recorded
 * before `clientInfo` exists, so a capture-time project-aware scrub is
 * impossible, and an assembly-time-only scrub is too late for a buffer that
 * already holds the secret.
 */

import { normalizeClientInfo } from './client-info.js';

function escapeRegExp(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function isMeaningfulValue(value) {
  return typeof value === 'string' && value.trim().length >= 4;
}

function collectClientPIIValues(clientInfo, projectName = 'Untitled') {
  const client = normalizeClientInfo(clientInfo, projectName);
  const out = [];

  if (isMeaningfulValue(projectName) && projectName.trim() !== 'Untitled')
    out.push({ label: 'project-name', value: projectName.trim() });
  if (isMeaningfulValue(client.name) && client.name !== 'Untitled')
    out.push({ label: 'client-name', value: client.name });
  if (isMeaningfulValue(client.tagline)) out.push({ label: 'tagline', value: client.tagline });
  if (isMeaningfulValue(client.phone)) out.push({ label: 'phone', value: client.phone });
  if (isMeaningfulValue(client.phoneFormatted))
    out.push({ label: 'phone', value: client.phoneFormatted });
  if (isMeaningfulValue(client.email)) out.push({ label: 'email', value: client.email });
  if (isMeaningfulValue(client.domain)) out.push({ label: 'domain', value: client.domain });

  if (isMeaningfulValue(client.address?.lineOne))
    out.push({ label: 'address', value: client.address.lineOne });
  if (isMeaningfulValue(client.address?.lineTwo))
    out.push({ label: 'address', value: client.address.lineTwo });
  if (isMeaningfulValue(client.address?.city))
    out.push({ label: 'address', value: client.address.city });
  if (isMeaningfulValue(client.address?.county))
    out.push({ label: 'address', value: client.address.county });
  if (isMeaningfulValue(client.address?.postcode))
    out.push({ label: 'address', value: client.address.postcode });
  if (isMeaningfulValue(client.address?.mapLink))
    out.push({ label: 'address', value: client.address.mapLink });

  for (const key of ['facebook', 'instagram', 'linkedin', 'x', 'youtube', 'yelp']) {
    const value = client.social?.[key];
    if (isMeaningfulValue(value)) out.push({ label: key, value });
  }

  // One value can legitimately occupy more than one profile field (the
  // default client name is the project name). Redact longest values first so a
  // shorter alias cannot leave the tail of a longer sensitive value behind.
  const seen = new Set();
  return out
    .filter((entry) => {
      const key = entry.value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => b.value.length - a.value.length);
}

/**
 * Credential shapes, scrubbed with NO project context — so this can run the
 * instant a string is captured. Modelled on `export/scaffolds.js`'s
 * `SECRET_SHAPES`, and ordered longest-prefix-first so a more specific pattern
 * cannot be pre-empted by a general one.
 *
 * Deliberately shape-based, not value-based: a diagnostics buffer never knows
 * which secrets exist, so it cannot blocklist them by value.
 */
export const SECRET_SHAPES = Object.freeze([
  [/\bpsk_[A-Za-z0-9_-]{8,}/g, '[REDACTED:api-key]'],
  [/\bsk-ant-[A-Za-z0-9_-]{8,}/g, '[REDACTED:api-key]'],
  [/\bsk-[A-Za-z0-9]{20,}/g, '[REDACTED:api-key]'],
  [/\bgh[pousr]_[A-Za-z0-9]{16,}/g, '[REDACTED:token]'],
  [/\bAKIA[0-9A-Z]{16}\b/g, '[REDACTED:aws-key]'],
  // `Authorization: Bearer <...>` and friends, header or JSON.
  [/\b[Bb]earer\s+[A-Za-z0-9._~+/-]{16,}=*/g, 'Bearer [REDACTED:token]'],
  // A JWT is three base64url segments; distinctive enough to match on shape.
  [/\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/g, '[REDACTED:jwt]']
]);

/**
 * Context-free scrub: secrets, then the SHAPES of emails and phone numbers.
 * Safe to run at capture time, before any project is loaded.
 */
export function scrubGeneric(text) {
  if (typeof text !== 'string' || !text) return '';
  let next = String(text);
  for (const [pattern, replacement] of SECRET_SHAPES) next = next.replace(pattern, replacement);
  next = next.replace(/mailto:[^\s"'<>]+/gi, '[REDACTED:email]');
  next = next.replace(/tel:[^\s"'<>]+/gi, '[REDACTED:phone]');
  next = next.replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[REDACTED:email]');
  next = next.replace(/\+?\d[\d\s().-]{6,}\d/g, '[REDACTED:phone]');
  return next;
}

/**
 * Scrub likely PII from freeform text while preserving enough structure for debugging.
 */
export function scrubRewriteText(text, options = {}) {
  if (typeof text !== 'string' || !text) return '';
  const { clientInfo, projectName = 'Untitled' } = options;

  // Secrets + context-free PII shapes.
  let next = scrubGeneric(text);

  // Project-specific values (identity, tagline, contact details and socials).
  const piiValues = collectClientPIIValues(clientInfo, projectName);
  for (const entry of piiValues) {
    const pattern = new RegExp(escapeRegExp(entry.value), 'gi');
    next = next.replace(pattern, `[REDACTED:${entry.label}]`);
  }

  return next;
}

function scrubUnknown(value, options) {
  if (typeof value === 'string') return scrubRewriteText(value, options);
  if (Array.isArray(value)) return value.map((item) => scrubUnknown(item, options));
  if (value && typeof value === 'object') {
    const out = {};
    for (const [key, v] of Object.entries(value)) out[key] = scrubUnknown(v, options);
    return out;
  }
  return value;
}

/**
 * Deep scrub structured payloads before fixture capture.
 */
export function scrubRewritePayload(payload, options = {}) {
  return scrubUnknown(payload, options);
}
